/**
 * Phase-1 hard-coded category catalog (mirrors STOPS_MANAGEMENT_API_SPEC §5.1).
 *
 * The service merges the BE response from GET /v3/stops/categories on top of
 * these defaults so the FE always has labels and pin colours available even
 * before BE ships.
 */

export const STOP_CATEGORY_KEYS = {
  REGULAR: 'REGULAR',
  PARK_AND_RIDE: 'PARK_AND_RIDE',
  CAMPUS_STOPS: 'CAMPUS_STOPS',
  BUS_STOPS: 'BUS_STOPS',
  DEPOT: 'DEPOT',
};

/** Every scheduled-stop module the BE knows (`VTFStopModule`). */
export const STOP_MODULES = ['SHUTTLE', 'VANPOOL'];

/**
 * Left-panel tabs on the Stops page. The tab drives the list query on the
 * server (see store `tabListParams`), not a client-side filter of one page.
 */
export const STOP_TABS = {
  REGULAR: 'regular',
  ON_DEMAND: 'onDemand',
};

/**
 * Most stops "Show all" loads into the list and map. Every stop is its own map
 * marker (plus coverage overlay), and the BE's own bbox page defaults to 500.
 */
export const STOPS_SHOW_ALL_LIMIT = 500;

/** Virtual group keys returned by GET /v3/stops/search (§3.5). */
export const STOP_SEARCH_VIRTUAL_KEYS = {
  ON_DEMAND: 'ON_DEMAND',
  UNIVERSAL: 'UNIVERSAL',
};

export const STOP_CATEGORY_DEFAULTS = [
  {
    key: 'REGULAR',
    label: { en: 'Regular', tr: 'Standart' },
    pinColor: '#FF7856',
    available: true,
  },
  {
    key: 'PARK_AND_RIDE',
    label: { en: 'Park & Ride', tr: 'Park & Ride' },
    pinColor: '#009A09',
    available: true,
  },
  {
    key: 'CAMPUS_STOPS',
    label: { en: 'Campus Stops', tr: 'Kampüs Durakları' },
    pinColor: '#735AE1',
    available: true,
  },
  {
    key: 'BUS_STOPS',
    label: { en: 'Bus Stops', tr: 'Otobüs Durakları' },
    pinColor: '#42ACFF',
    available: true,
  },
  {
    key: 'DEPOT',
    label: { en: 'Depots', tr: 'Garajlar' },
    pinColor: '#888888',
    available: false,
  },
];

/** Virtual labels for search-only group keys. Not real categories. */
export const STOP_SEARCH_VIRTUAL_DEFAULTS = [
  {
    key: 'ON_DEMAND',
    label: { en: 'On Demand', tr: 'Talep Üzerine' },
    pinColor: '#9AA0A6',
  },
  {
    key: 'UNIVERSAL',
    label: { en: 'Universal Stops', tr: 'Evrensel Duraklar' },
    pinColor: '#9AA0A6',
  },
];

/**
 * Resolve a category key to its pin colour. Looks through the real category
 * defaults first, then the search-only virtual groups. Returns `null` for an
 * unknown/empty key so callers can fall back (e.g. to black for non-POI stops).
 */
export function categoryPinColor(key) {
  if (!key) return null;
  const found =
    STOP_CATEGORY_DEFAULTS.find((c) => c.key === key) ||
    STOP_SEARCH_VIRTUAL_DEFAULTS.find((c) => c.key === key);
  return found?.pinColor || null;
}

/**
 * Resolve a category key to its localized label (falls back to EN, then the key).
 */
export function categoryLabel(key, lang = 'en') {
  if (!key) return '';
  const found =
    STOP_CATEGORY_DEFAULTS.find((c) => c.key === key) ||
    STOP_SEARCH_VIRTUAL_DEFAULTS.find((c) => c.key === key);
  if (!found) return key;
  return found.label?.[lang] || found.label?.en || key;
}

/**
 * Merge BE-returned categories on top of defaults; preserves order from defaults
 * and appends any extras at the end.
 */
export function mergeCategories(beCategories) {
  if (!Array.isArray(beCategories) || beCategories.length === 0) {
    return STOP_CATEGORY_DEFAULTS.map((c) => ({ ...c }));
  }

  const beMap = new Map(beCategories.map((c) => [c.key, c]));
  const merged = STOP_CATEGORY_DEFAULTS.map((def) => {
    const be = beMap.get(def.key);
    if (!be) return { ...def };
    beMap.delete(def.key);
    return {
      ...def,
      ...be,
      label: { ...def.label, ...(be.label || {}) },
    };
  });

  beMap.forEach((extra) => merged.push(extra));
  return merged;
}

export default {
  STOP_CATEGORY_KEYS,
  STOP_MODULES,
  STOP_TABS,
  STOP_SEARCH_VIRTUAL_KEYS,
  STOP_CATEGORY_DEFAULTS,
  STOP_SEARCH_VIRTUAL_DEFAULTS,
  mergeCategories,
  categoryPinColor,
  categoryLabel,
};
